
import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";

import {
    ErrorResponseHelper,
    EntityState,
    Entity,
    uiStateActions,
    DOMAIN_TYPES,
    DomainRedux,
    DOMAIN_CONFIGS,
    DomainType,
    processFetchSinceData,
    ActionResponse,
    ActionType,
    EntityActionsRedux,
    SoNumber,
} from "@/Shared";

import { EntityService } from "../Services";

import { RootState, useAppDispatch, useAppSelector, } from "../Redux";


const NO_FEEDBACK_ACTIONS: ActionType[] = [
   'fetchSinceDate'
];
      
/**
 * =====================================================
 * Initial responses
 * =====================================================
 *
 * Default action state for all supported ActionTypes.
 * Tracks `status` and optional `error` for each action.
 * Allows components to monitor loading/error states per operation.
 */
const initialResponses: Record<ActionType, ActionResponse> = {
    // 🔹 API-only
    createEntity: { status: 'idle' },
    updateEntity: { status: 'idle' },
    deleteEntity: { status: 'idle' },
    restoreEntity: { status: 'idle' },
    fetchEntity: { status: 'idle' },
    fetchAllEntities: { status: 'idle' },
    fetchSinceDate: { status: 'idle' },

    // 🔹 Store-only
    addEntity: { status: 'idle' },
    setEntity: { status: 'idle' },
    removeEntity: { status: 'idle' },
    getEntity: { status: 'idle' },
    getEntityList: { status: 'idle' },

    // 🔹 Cache-first
    findEntity: { status: 'idle' },
    findAllEntities: { status: 'idle' },
    findCurrentEntity: { status: 'idle' },
    clearData: { status: 'idle' },
    setReadStatus: { status: 'idle' },

    search : {status : 'idle'},
    export : {status : 'idle'},
    generate : {status : 'idle'}
};

/**
 * =====================================================
 * useEntityActions hook
 * =====================================================
 *
 * Custom hook for managing entity state and performing CRUD operations.
 *
 * Responsibilities:
 * - Centralized Redux dispatching
 * - CRUD operations via entityService
 * - Cache-first logic (store first, fallback to API)
 * - Status/error tracking per action
 * - Mode handling ('active' vs 'trash')
 *
 * @template T - entity type (must have 'id' field)
 * @param selectSlice - selector for entity slice from Redux store
 * @param actionsRedux - Redux actions for the entity
 * @param entityService - API service for entity operations
 *
 * @returns
 * - `responses`: map of ActionType → ActionResponse
 * - `execute(action, payload?)`: unified function to trigger any action
 * - `setResponse(action, response)`: manually update action response
 * 
 * 
 * Author: Samano CASTRE
 * Date: February 01, 2026
 */
export function useEntityActions<T extends Entity>(
    selectSlice: (state: RootState) => EntityState<T>,
    actionsRedux: EntityActionsRedux<T>,
    entityService: EntityService<T>, pollerKey? : DomainRedux) {

    
    /** =====================================================
     * Redux and local state
     * =====================================================
     *
     * - `dispatch`: Redux dispatcher for all entity actions
     * - `entityState`: current slice of entity state
     * - `responses`: local state tracking status/error per action
     */
    const dispatch = useAppDispatch();
    const entityState = useAppSelector(selectSlice);
    const activeDomainDetail  = useAppSelector(state => state.uiState.activeDomainDetail );
    

    const [responses, setResponses] = useState<Record<ActionType, ActionResponse>>(initialResponses);


    /**
     * =====================================================
     * Route param parsing
     * =====================================================
     *
     * Extracts `id` from route and converts to number.
     * Used for current entity operations.
     */
    const { id } = useParams();
    const queryId = SoNumber.toNumber(id);

    
    /**
     * =====================================================
     * setResponse helper
     * =====================================================
     *
     * Updates response state for a specific action.
     * Preserves previous state of other actions.
     */
    const setResponse = useCallback((action: ActionType, response: ActionResponse) => {
        setResponses(prev => ({ ...prev, [action]: response }));
    }, []);

    
     /**
     * =====================================================
     * Current mode
     * =====================================================
     *
     * Tracks the current entity mode (active/trash)
     * for CRUD and state operations.
     */
    const currentMode = entityState.mode;


    /** =====================================================
     * handleCreateEntity
     * =====================================================
     *
     * Creates a new entity via the API service.
     * Responsibilities:
     * - Calls `entityService.create` if available
     * - Dispatches `setItem` to add entity to Redux store
     * - Returns the newly created entity
     */
    const handleCreateEntity = useCallback(async (payload: any) => {
        if (!entityService.create) {
            return;
        }
        const data = (await entityService.create(payload.values as FormData | Partial<T>, payload.idempotencyKey)).data;
        dispatch(actionsRedux.setItem({ item: data }));
        return data;
    }, [entityService, actionsRedux, dispatch]);


    /** =====================================================
     * handleUpdateEntity
     * =====================================================
     *
     * Updates an existing entity via the API service.
     * Responsibilities:
     * - Calls `entityService.update` if available
     * - Dispatches `setItem` to update entity in Redux store
     * - Returns the updated entity
     */
    const handleUpdateEntity = useCallback(async (payload: any) => {
        if (!entityService.update) {
            return;
        }
        const data = (await entityService.update(payload.id, payload.values as T | FormData)).data;
        dispatch(actionsRedux.setItem({ item: data }));
        return data;
    }, [entityService, actionsRedux, dispatch]);

    
    /** =====================================================
     * handleRestoreEntity
     * =====================================================
     *
     * Restores a deleted entity via the API service.
     * Responsibilities:
     * - Calls `entityService.restore` if available
     * - Removes entity from 'trash' and adds it back to 'active' mode in Redux
     * - Returns the restored entity
     */
    const handleRestoreEntity = useCallback(async (payload: any) => {
        if (!entityService.restore) {
            return;
        }
        const data = (await entityService.restore(payload)).data;
        dispatch(actionsRedux.removeItem({ key: payload, mode: 'trash' }));
        dispatch(actionsRedux.setItem({ item: data, mode: 'active' }));

        return data;
    }, [entityService, actionsRedux, dispatch]);


    /** =====================================================
     * handleFetchEntity
     * =====================================================
     *
     * Fetches a single entity via API, ignoring the store.
     * Responsibilities:
     * - Calls `entityService.fetchOne` if available
     * - Returns fetched entity
     */
    const handleFetchEntity = useCallback(async (payload: any) => {
        if (!entityService.fetchOne) {
            return;
        }
        const data = (await entityService.fetchOne(payload)).data;
        const mode = !!data.deletedAt ? 'trash' : 'active';
        dispatch(actionsRedux.setItem({ item: data, mode }));
        return data;
        
    }, [entityService, actionsRedux, dispatch]);


    /** =====================================================
     * handleFetchAllEntities
     * =====================================================
     *
     * Fetches all entities for the given mode.
     * Responsibilities:
     * - Calls `entityService.fetchAll` if available
     * - Dispatches `setLoading` before API call
     * - Updates Redux store with fetched list
     * - Returns fetched data
     */
    const handleFetchAllEntities = useCallback(async (payload: any) => {
        if (!entityService.fetchAll) {
            return;
        }
        dispatch(actionsRedux.setLoading(payload ?? currentMode));
        const data = (await entityService.fetchAll(payload ?? currentMode)).data;
        dispatch(actionsRedux.setList({ items: data, mode: payload ?? currentMode }));
        return data;
    }, [entityService, actionsRedux, currentMode, dispatch]);


    /** =====================================================
     * handleFetchSinceDate
     * =====================================================
     *
     * Fetches entities updated since a given date.
     * Responsibilities:
     * - Calls `entityService.fetchSinceDate` if available
     * - Separates active vs deleted items
     * - Updates 'active' and 'trash' lists in Redux
     * - Returns fetched data
     */
    const handleFetchSinceDate = useCallback(async (payload: any) => {
        if (!entityService.fetchSinceDate || !pollerKey) {
            return;
        }
        const since = payload as string || new Date().toISOString();
        const data = (await entityService.fetchSinceDate(since)).data;
        const {  activeItems,  deletedItems,  badgeItems  } = processFetchSinceData(data, since);

        const hasActiveItems = activeItems?.length > 0;
        const hasDeletedItems = deletedItems?.length > 0;

        const shouldDisplayBadge = (pollerKey in DOMAIN_TYPES) ? !DOMAIN_CONFIGS[pollerKey as DomainType]?.desableBadge : false;

        if (hasActiveItems) {
            dispatch(actionsRedux.addList({ items: activeItems, mode: 'active' }));
            dispatch(actionsRedux.removeList({ items: activeItems, mode: 'trash' }));
                
            if (shouldDisplayBadge) {
                if (pollerKey === activeDomainDetail ) {
                    if(queryId) {
                        dispatch(uiStateActions.removeBadge({
                            key : pollerKey, 
                            values: [queryId]
                        }));
                    }
                }
                else {
                    dispatch(uiStateActions.updateBadge({
                        key : pollerKey, 
                        values: badgeItems
                    }));
                }
            }
        }
        if (hasDeletedItems) {
            
            dispatch(actionsRedux.removeList({ items: deletedItems, mode: 'active' }));
            dispatch(actionsRedux.addList({ items: deletedItems, mode: 'trash' }));

            if (shouldDisplayBadge) {
                dispatch(uiStateActions.removeBadge({
                    key : pollerKey, 
                    values: deletedItems.map(item => item.id as number)
                }));
            }
        }
        return data;
    }, [
        entityService, 
        actionsRedux, 
        dispatch, 
        activeDomainDetail,
        pollerKey
    ]);


    /** =====================================================
     * handleFindEntity
     * =====================================================
     *
     * Retrieves an entity from the store first, falls back to API.
     * Responsibilities:
     * - Looks up entity in Redux store by current mode
     * - Calls `entityService.fetchOne` if not found
     * - Updates Redux store with fetched entity
     * - Returns the entity
     */
    const handleFindEntity = useCallback(async (payload: any) => {
        let data = entityState[currentMode]?.keyPairData?.[payload];

        if (!data && entityService.fetchOne) {
            data = (await entityService.fetchOne(payload)).data;
            dispatch(actionsRedux.setItem({ item: data }));
        }
        return data;
    }, [entityService, actionsRedux, currentMode, dispatch]);


    /** =====================================================
     * handleFindAllEntities
     * =====================================================
     *
     * Retrieves all entities from the store first, falls back to API.
     * Responsibilities:
     * - Collects all entities from Redux store by current mode
     * - Calls `entityService.fetchAll` if store is empty
     * - Updates Redux store with fetched list
     * - Returns the entity list
     */
    const handleFindAllEntities = useCallback(async (payload: any) => {
        const keyPairDataFound = entityState[currentMode]?.keyPairData;
        let data = keyPairDataFound ? Object.values(keyPairDataFound).filter(Boolean) : [];

        if ((!data || data.length === 0) && entityService.fetchAll) {
            data = (await entityService.fetchAll(currentMode)).data;
            dispatch(actionsRedux.setList({ items: data, mode: currentMode }));
        }
        return data;
    }, [entityService, actionsRedux, currentMode, dispatch]);

    const handleSearch = useCallback(
        async (payload: any) => await entityService.search?.(payload), 
        [entityService]
    );
    const handleExport = useCallback(
        async (payload: any) => await entityService.export?.(payload.params, payload.format), 
        [entityService]
    );
    const handleGenerate = useCallback(
        async (payload: any) => await entityService.generate?.(payload.params, payload.format), 
        [entityService]
    );


    /** =====================================================
     * handleGetCurrentEntity
     * =====================================================
     *
     * Retrieves the current entity based on route `id`.
     * Responsibilities:
     * - Uses `queryId` from route params
     * - Fetches entity using `handleFindEntity`
     * - Returns null if `queryId` is missing
     */
    const handleGetCurrentEntity = useCallback(async (payload: any) => {
        if (!queryId) {
            return null; //Important
        }
        return await handleFindEntity(queryId);
    }, [entityService, actionsRedux, dispatch, queryId]);


    /** =====================================================
     * handleDeleteEntity
     * =====================================================
     *
     * Deletes an entity via the API service and manages trash logic.
     * Responsibilities:
     * - Calls `entityService.delete` if available
     * - Removes the entity from 'active' mode in Redux
     * - If restoration is allowed (`payload.canRestore`):
     *   - Adds the deleted entity to 'trash' mode in Redux immediately
     *   - Triggers asynchronous fetch to update trash entities (fire-and-forget)
     * - Returns true immediately after removal from 'active' mode
     */
    const handleDeleteEntity = useCallback(async (payload: any) => {
        if (!entityService.delete) {
            return;
        }
        await entityService.delete(payload.id);

        //save the deleted item;
        const deletedItem = entityState['active']?.keyPairData?.[payload.id];

        dispatch(actionsRedux.removeItem({ key: payload.id, mode: 'active' }));

        if (payload.canRestore) {
           
            dispatch(actionsRedux.setItem({ item : deletedItem, mode: 'trash' }));
        
            const lastUpdatedAt = entityState.trash?.lastUpdatedAt;
            if (lastUpdatedAt) {
                void handleFetchSinceDate(lastUpdatedAt);
            }
            else {
                void handleFetchAllEntities('trash');
            }
        }
        return true;
    }, [entityService, actionsRedux, dispatch]);


    /** =====================================================
     * execute
     * =====================================================
     *
     * Unified executor for all supported ActionTypes.
     * Handles:
     * - API calls (if available)
     * - Redux store updates
     * - Cache-first logic (store first, fallback to API)
     * - Status and error tracking per action
     * - Mode management ('active' vs 'trash')
     */
    const execute = useCallback(
        async (action: ActionType, payload?: any) => {
            
            const shouldFeedback = !NO_FEEDBACK_ACTIONS.includes(action);
            shouldFeedback && setResponse(action, { status: 'loading' });

            try {
                let data: any;

                switch (action) {
                    // 🔹 API-only actions
                    case 'createEntity': data = await handleCreateEntity(payload);  break;
                    case 'updateEntity': data = await handleUpdateEntity(payload); break;
                    case 'restoreEntity': data = await handleRestoreEntity(payload); break;
                    case 'deleteEntity': data = await handleDeleteEntity(payload); break;
                    case 'fetchEntity': data = await handleFetchEntity(payload); break;
                    case 'fetchAllEntities': data = await handleFetchAllEntities(payload); break;
                    case 'fetchSinceDate': data = await handleFetchSinceDate(payload);  break;
                    case 'search': data = await handleSearch(payload);  break;
                    case 'export': data = await handleExport(payload);  break;
                    case 'generate': data = await handleGenerate(payload);  break;

                    // 🔹 Store-only actions
                    case 'addEntity':
                        data = payload;
                        dispatch(actionsRedux.setItem({ item: data }));
                        break;

                    case 'setEntity':
                        data = payload;
                        dispatch(actionsRedux.setItem({ item: data }));
                        break;

                    case 'removeEntity':
                        dispatch(actionsRedux.removeItem({ key: payload, mode: currentMode }));
                        break;

                    case 'clearData':  dispatch(actionsRedux.clearData()); break;
                    case 'setReadStatus':
                        dispatch(actionsRedux.setReadStatus({
                            isRead: payload as boolean,
                            mode: currentMode
                        }));
                        break;

                    case 'getEntity': data = entityState[currentMode]?.keyPairData?.[payload]; break;

                    case 'getEntityList':
                        const keyPairData = entityState[currentMode]?.keyPairData;
                        data = keyPairData ? Object.values(keyPairData).filter(Boolean) : [];
                        break;

                    case 'findEntity': data = await handleFindEntity(payload); break;
                    case 'findAllEntities': data = await handleFindAllEntities(payload); break;
                    case 'findCurrentEntity': data = await handleGetCurrentEntity(payload); break;
                }

                shouldFeedback && setResponse(action, { status: 'success' });
                return data;
            } catch (err: any) {
                if(!shouldFeedback) {
                    return undefined;
                }
                const actionResponse = ErrorResponseHelper.getError(err);

                setResponse(action, actionResponse as ActionResponse);
                if (['fetchAllEntities', 'findAllEntities'].includes(action)) {
                    dispatch(actionsRedux.setError({
                        error: actionResponse.error,
                        mode: currentMode
                    }));
                }
                return undefined;
            }
        },
        [
            entityService,
            actionsRedux,
            setResponse,
            entityState[currentMode],
            currentMode,
            id,
            handleCreateEntity,
            handleUpdateEntity,
            handleDeleteEntity,
            handleRestoreEntity,

            handleFindEntity,
            handleFindAllEntities,

            handleFetchEntity,
            handleGetCurrentEntity,

            handleFetchAllEntities,
            handleFetchSinceDate,

            handleSearch,
            handleExport,
            handleGenerate
        ]
    );

    /** =====================================================
     * Return API
     * =====================================================
     *
     * Exposes:
     * - `queryId`: numeric route ID for current entity
     * - `responses`: map of ActionType → ActionResponse
     * - `execute`: main executor for all actions
     * - `setResponse`: manual response updater
     */
    return {
        queryId,
        responses,
        execute,
        setResponse
    };
}